/**
 * Jobs Page
 *
 * Displays job queue with EITELKEIT components.
 * Uses JobQueuePanel for queue view and LiveLogs for streaming logs.
 */

import type { CSSProperties } from 'react';
import { JobQueuePanel, LiveLogs } from '@brutal/brutal/organisms';
import { tokens } from '../local-components';
import { useJobEvents } from '../hooks/useJobEvents';
import { useAgent } from '../hooks/useAgent';

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[6],
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes['2xl'],
  fontWeight: tokens.fontWeights.bold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const buttonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.void,
  backgroundColor: tokens.colors.mint,
  border: 'none',
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
  cursor: 'pointer',
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: tokens.colors.surface,
  color: tokens.colors.textMuted,
  cursor: 'not-allowed',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: tokens.spacing[6],
};

const logsContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[3],
};

const logsSectionTitleStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.xs,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
};

const loadingStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  padding: tokens.spacing[6],
  textAlign: 'center',
};

const errorStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.danger,
  padding: tokens.spacing[4],
  backgroundColor: 'rgba(255, 71, 87, 0.1)',
  border: `1px solid ${tokens.colors.danger}`,
  borderRadius: tokens.radii.md,
};

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function Jobs() {
  const { isConnected } = useAgent();
  const { jobs, logs, isLoading, error, refreshJobs, clearLogs } = useJobEvents({
    maxLogs: 200,
    autoFetch: true,
  });

  const handleJobClick = (jobId: string) => {
    // TODO: Navigate to job details or show modal
    console.log('Job clicked:', jobId);
  };

  const handleRefresh = () => {
    refreshJobs();
  };

  if (!isConnected) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Jobs</h1>
        <div style={loadingStyle}>Connecting to agent...</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Jobs</h1>
        <div style={loadingStyle}>Loading jobs...</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Jobs</h1>
        <button
          onClick={handleRefresh}
          disabled={!isConnected}
          style={isConnected ? buttonStyle : buttonDisabledStyle}
        >
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && <div style={errorStyle}>Error: {error}</div>}

      {/* Main content grid */}
      <div style={gridStyle}>
        {/* Job Queue Panel */}
        <JobQueuePanel jobs={jobs} onJobClick={handleJobClick} style={null} />

        {/* Live Logs */}
        <div style={logsContainerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={logsSectionTitleStyle}>Live Logs</span>
            <button
              onClick={clearLogs}
              style={{
                fontFamily: tokens.fonts.mono,
                fontSize: tokens.fontSizes.xs,
                color: tokens.colors.textMuted,
                backgroundColor: 'transparent',
                border: `1px solid ${tokens.colors.border}`,
                borderRadius: tokens.radii.sm,
                padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </div>
          <LiveLogs logs={logs} autoScroll={true} maxLines={200} style={{ height: '400px' }} />
        </div>
      </div>
    </div>
  );
}
